// File: src/components/forms/DateField/DateField.tsx
// Purpose: Form input component

import React, { useState } from "react";
import { Calendar } from "lucide-react";
import { TextField, TextFieldProps } from "../TextField";

/** 📌 Formats a date to `YYYY-MM-DD` */
const formatDate = (value: string) => {
  const cleaned = value.replace(/\D/g, ""); // Remove non-numeric chars
  if (cleaned.length <= 4) return cleaned; // Year input
  if (cleaned.length <= 6) return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
  return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 6)}-${cleaned.slice(6, 8)}`; // Full YYYY-MM-DD format
};

/** 📌 Validates if the date is within range */
const validateDate = (value: string, minDate?: string, maxDate?: string) => {
  if (!value) return null; // ✅ No error when empty
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(value)) return "Invalid date format (YYYY-MM-DD)";

  const date = new Date(value);
  if (isNaN(date.getTime())) return "Invalid date";

  if (minDate && date < new Date(minDate)) return `Date must be after ${minDate}`;
  if (maxDate && date > new Date(maxDate)) return `Date must be before ${maxDate}`;

  return null; // ✅ No error
};

export interface DateFieldProps extends Omit<TextFieldProps, "type"> {
  minDate?: string; // ✅ Min selectable date
  maxDate?: string; // ✅ Max selectable date
  startDate?: string; // ✅ Default start date
}

export const DateField: React.FC<DateFieldProps> = ({
  minDate,
  maxDate,
  startDate,
  ...props
}) => {
  const [dateValue, setDateValue] = useState<string>(startDate || ""); // ✅ Default start date
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  return (
    <TextField
      {...props}
      type="date"
      icon={Calendar}
      placeholder="YYYY-MM-DD"
      value={dateValue}
      min={minDate}
      max={maxDate}
   
      onChange={(e) => {
        const formatted = formatDate(e.target.value);
        setDateValue(formatted);

        if (touched) {
          setError(validateDate(formatted, minDate, maxDate));
        }
      }}
      onBlur={() => {
        setTouched(true);
        setError(validateDate(dateValue, minDate, maxDate));
      }}
      error={touched ? error : undefined}
    />
  );
};